import React, { useState, useEffect } from 'react';
import { Box, Text, Flex, Card, Badge, Avatar, IconButton } from '@radix-ui/themes';
import { QuestionMarkCircledIcon, PersonIcon, TargetIcon, Cross2Icon, CrossCircledIcon, CheckIcon } from '@radix-ui/react-icons';
import { reportService } from '../../../services/teacher/reportService';
import LoadingIndicator from '../../shared/LoadingIndicator';
import CircleProgress from '../../shared/CircleProgress';

const TeacherQuizReport = ({quizId, onClose}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quizData, setQuizData] = useState(null);

    useEffect(() => {
        const fetchQuizReport = async () => {
            try {
                setLoading(true);
                const data = await reportService.getQuizReport(quizId);
                console.log('quiz report data>>>>>', data);
                setQuizData(data);
                setError(null);
            } catch (err) {
                setError('Failed to load quiz report');
                console.error('Error fetching quiz report:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchQuizReport();
    }, [quizId]);

    if (loading) {
        return (
            <Flex align="center" justify="center" p="4">
                <LoadingIndicator />
            </Flex>
        );
    }

    if (error) {
        return (
            <Flex align="center" justify="center" p="4">
                <Text color="red">Error: {error}</Text>
            </Flex>
        );
    }

    const { quiz, quiz_report } = quizData;

    const studentsAttempted = quiz_report.filter(student => 
        student.quiz_attempts.length > 0
    ).length;

    const averageFirstAttemptScore = quiz_report
        .flatMap(student => student.quiz_attempts)
        .filter(attempt => attempt?.first_attempt)
        .reduce((acc, attempt) => acc + attempt.percentage, 0) / studentsAttempted || 0;

    return (
        <Box>
            <Flex justify="between" align="center" mb="4">
                <Text size="3" weight="bold">Quiz Report</Text>
                <IconButton 
                    variant="ghost" 
                    color="gray" 
                    onClick={onClose}
                    aria-label="Close report"
                >
                    <Cross2Icon />
                </IconButton>
            </Flex>
            
            <Card>
                <Flex direction="column" justify="center" align="center" gap="3" mb="4">
                    <CircleProgress
                        progress={averageFirstAttemptScore}
                        size={100}
                        strokeWidth={5}
                        label={`${Math.floor(averageFirstAttemptScore)}%`}
                        labelSize="4"
                    />
                    <Text size="3">Average First Attempt Score</Text>
                    <Flex direction="row" gap="3">
                        <Flex gap="1" align="center">
                            <QuestionMarkCircledIcon />
                            <Text size="2" color="gray">Questions</Text>
                            <Badge size="2" color="blue">{quiz.questions.length}</Badge>
                        </Flex>
                        <Flex gap="1" align="center">
                            <PersonIcon />
                            <Text size="2" color="gray">Students Attempted</Text>
                            <Badge size="2" color="orange">{studentsAttempted}</Badge>
                        </Flex>
                    </Flex>
                </Flex>
            </Card>

            <Box mt="4">
                {/* <Text size="3" weight="bold" mb="3">Student Performance</Text> */}
                <Flex direction="column" gap="3">
                    {quiz_report.map((student) => {
                        const firstAttempt = student.quiz_attempts.find(attempt => attempt.first_attempt);
                        const bestAttempt = student.quiz_attempts.reduce((best, current) => 
                            (current.percentage > (best?.percentage || 0)) ? current : best
                        , null);

                        const studentName = student.user ? 
                            `${student.user.firstname} ${student.user.lastname}` : 
                            `Student #${student.user_id}`;
                        
                        const nameInitial = student.user?.firstname?.[0]?.toUpperCase() || 'S';

                        return (
                            <Flex direction="column" key={student.id} p="3" style={{ borderBottom: '1px solid #eee' }}>
                                <Flex direction="row" justify="between" align="center">
                                    <Flex direction="row" gap="3" align="center">
                                        <Avatar
                                            size="3"
                                            src={student.user?.profile_picture || undefined}
                                            fallback={nameInitial}
                                            radius="full"
                                        />
                                        <Flex direction="column" align="start" gap="1">
                                            <Text weight="bold">
                                                {studentName}
                                            </Text>
                                            <Badge>{student.quiz_attempts.length} attempts</Badge>
                                        </Flex>
                                    </Flex>
                                    {firstAttempt ? (
                                        <Flex direction="column" gap="1" align="end">
                                            <Text size="2" color="gray">
                                                First Attempt: {firstAttempt.percentage}%
                                            </Text>
                                            <Text size="2" color="gray">
                                                Best Score: {bestAttempt ? `${bestAttempt.percentage}%` : 'Not attempted'}
                                            </Text>
                                        </Flex>
                                        ):(
                                            <CrossCircledIcon color="gray" width="30" height="30" />
                                        )}
                                </Flex>
                                {firstAttempt && (
                                    <Flex direction="row" gap="1" p="0" justify="end" align="center">
                                        {firstAttempt.question_attempts.map(
                                            (questionAttempt, index) => (
                                                <span key={index} className="xs-round-icon" style={{ backgroundColor: questionAttempt.is_correct ? 'var(--green-3)' : 'var(--red-3)'}}>
                                                    {questionAttempt.is_correct ? <CheckIcon width="15" height="15" color="var(--gray-12)" /> : <Cross2Icon width="15" height="15" color="var(--gray-12)" />}
                                                </span>
                                            )
                                        )}
                                    </Flex>
                                )}
                            </Flex>
                        );
                    })}
                </Flex>
            </Box>
        </Box>
    );
};

export default TeacherQuizReport;
